<script>
import AlertBannerComponent from './AlertBanner.vue'
import BtnComoponent from './Btn.vue'
import DateFieldsComponent from './DateFields.vue'
import DropdownComponent from './Dropdown.vue'
import GuestFieldsComponent from './GuestFields.vue'
import RatePlanToggleComponent from './RatePlanToggle.vue'
import DisabledTipComponent from './DisabledTip.vue'
import debounce from 'lodash/debounce'
import { marked } from 'marked'
import store from '../store.js'
import { breakpointMap } from '../breakpoint'
import { get as getAvailability, getPartyAvailability, getPartyParams } from '../services/availability'
import { getDiffDays, getDateString, todayAt } from '../date'
import { getDefaultDeparture, getStayRuleNote, isRuleStay, MAX_NIGHTS } from '../stayRules'

let uuid = 0

export default {
    // state
    data () {
        const id = uuid++
        const submitBtnId = `vca_vue_prop_page_search_submit_btn_` + id
        return {
            action: null,
            loading: null,
            stayLengthMax: MAX_NIGHTS,
            resultsHTML: null,
            resultsError: null,
            store,
            submitBtnId,
            // availability
            availability: null,
            hasAvailabilityData: null,
            isGettingAvailability: null,
            partyAvailability: null,
            stayRules: null,
            // 'Y-m' the calendar last showed; the rule banner describes those months
            visibleMonths: null,
            // date
            arrival: null,
            departure: null,
            startDate: null,
            endDate: null,
            // property
            propertyRentalSourceId: null,
            propertyCode: null,
            // room types
            roomType: null,
            roomTypeOptions: null,
            // search params
            checkInDate: null,
            checkOutDate: null,
            numberOfGuests: null,
            numberOfChildren: null,
            childAges: [],
            rcId: null,
            // user
            userId: null,
            isPointsMember: null,
            loggedIn: null,
            points: null,
            // window
            breakpointMap,
            // lifecycle
            isMounted: false,
        }
    },
    props: {
        property: String,
        search: String,
        user: String,
        resultsErrorMarkdown: String,
    },
    computed: {
        // one warning above the form; the party's wins, since no choice of dates fixes it
        banner () {
            return this.partyWarning || getStayRuleNote(this.stayRules, this.bannerMonths)
        },
        // before the calendar opens: the chosen arrival's month and the next, else this month and the next
        bannerMonths () {
            if (this.visibleMonths) return this.visibleMonths
            const first = this.arrivalDate || this.startDate
            const next = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + 1))
            return [getDateString(first).slice(0, 7), getDateString(next).slice(0, 7)]
        },
        isTooLong () {
            return this.stayLength > this.stayLengthMax
        },
        isSearchDisabled () {
            return !this.arrival || !this.departure || !this.numberOfGuests || this.isTooLong
        },
        // no residence of the chosen room type sleeps the party
        partyWarning () {
            const { partySize, largestSleeps, residenceNoun } = this.partyAvailability ?? {}
            if (!partySize || !largestSleeps || partySize <= largestSleeps) {
                return null
            }
            const elsewhere = this.roomType === null ? 'another resort' : 'another room type'
            return `No ${residenceNoun} here sleeps ${partySize} guests. Reduce number of guests or choose ${elsewhere}.`
        },
        arrivalDate () {
            return this.arrival && new Date(this.arrival)
        },
        departureDate () {
            return this.departure && new Date(this.departure)
        },
        datefields () {
            return [
                {
                    display: this.checkInDate,
                    event: 'update-arrival',
                    key: 'arrival',
                    label: 'Arriving',
                    value: this.arrival,
                },
                {
                    display: this.checkOutDate,
                    event: 'update-departure',
                    key: 'departure',
                    label: 'Departing',
                    value: this.departure,
                },
            ]
        },
        guests () {
            return {
                adults: this.numberOfGuests,
                children: this.childAges,
            }
        },
        ratePlan () {
            if (this.hasAvailabilityData) {
                return this.points ? 'points' : 'usd'
            }
            return null
        },
        roomTypeDropdown () {
            const isFullWidth = store.breakpoint.includes('s')
            return {
                from: 'right',
                isFullWidth,
                label: 'Room Type',
                labelIsHidden: isFullWidth,
                options: this.roomTypeOptions,
                value: this.roomType,
            }
        },
        hasRatePlanToggle () {
            return this.loggedIn && this.isPointsMember
        },
        stayLength () {
            return this.getStayLength(this.arrivalDate, this.departureDate)
        },
        resultsErrorMarkup () {
            return this.resultsErrorMarkdown ? marked.parse(this.resultsErrorMarkdown) : null
        },
    },
    methods: {
        getStayLength (arrivalDate, departureDate) {
            if (arrivalDate && departureDate) {
                return getDiffDays([ departureDate, arrivalDate ])
            } else {
                return 0
            }
        },
        setRoomType (roomType) {
            this.roomType = roomType
            this.updateAvailability()
        },
        willSearch () {
            return this.stayLength >= 1 && this.stayLength <= this.stayLengthMax
        },
        submit (onPageLoad = false) {
            if (!this.$el || !this.willSearch()) return

            this.resultsHTML = null
            this.resultsError = false

            this.loading = true

            const url = new URL(window.location.origin)
            url.pathname = this.$el.querySelector('form').getAttribute('action')
            url.searchParams.append('checkInDate', this.checkInDate)
            url.searchParams.append('checkOutDate', this.checkOutDate)
            url.searchParams.append('numberOfGuests', this.numberOfGuests)
            url.searchParams.append('numberOfChildren', this.childAges.length)
            this.childAges.forEach(e => url.searchParams.append('childAges[]', e))
            url.searchParams.append('points', this.points)
            if (this.roomType !== null) {
                url.searchParams.append('minBedrooms', this.roomType)
            }
            // Google's rate plan id names an offer of the landing itinerary only
            if (onPageLoad && this.rcId) {
                url.searchParams.append('rcId', this.rcId)
            }

            if (!onPageLoad) {
                window?.history?.pushState(null, null, url.search)
            }

            const searchStartTime = performance.now()

            fetch(url)
                .then(response => {
                    if (!response.ok) {
                        this.loading = false;
                        this.resultsError = true;
                        return null;
                    }
                    return response.text();
                })
                .then(html => {
                    if (html !== null) {
                        const responseReceivedTime = performance.now();

                        this.loading = false;
                        this.resultsHTML = html;

                        this.$nextTick(() => {
                            // No Alpine.initTree() here: Alpine's MutationObserver already initializes
                            // injected content, and initTree() re-binds handlers on initialized DOM,
                            // double-firing the gtag events (see c6ab72c9).

                            window.posthog?.capture('rental_property_search_completed', {
                                search_duration_ms: Math.round(performance.now() - searchStartTime),
                                search_fetch_ms: Math.round(responseReceivedTime - searchStartTime),
                                search_render_ms: Math.round(performance.now() - responseReceivedTime),
                                on_page_load: onPageLoad,
                                property_id: this.propertyCode,
                                check_in_date: this.arrival,
                                check_out_date: this.departure,
                                number_of_guests: this.numberOfGuests,
                                number_of_children: this.childAges.length,
                                is_points_search: this.points === 1,
                            })
                        })
                    }
                })
                .catch(() => {
                    this.loading = false;
                    this.resultsError = true;
                })
        },
        updateAvailability () {
            if (!this.hasAvailabilityData) return
            this.isGettingAvailability = true
            const guests = store.guests
            const roomType = this.roomType
            const params = {
                propertyRentalSourceId: this.propertyRentalSourceId,
                numberOfBedrooms: roomType,
                startDate: getDateString(this.startDate),
                endDate: getDateString(this.endDate),
                ...getPartyParams(guests),
                withStayRules: 1,
            }
            if (typeof this.userId === 'number') {
                params.userId = this.userId
            }
            // a slower answer for an earlier party or room type must not overwrite a newer one
            const requestId = ++this.availabilityRequestId
            getAvailability({ params })
                .then(r => r.json())
                .then(json => {
                    if (requestId !== this.availabilityRequestId) return
                    this.availability = json.results
                    this.partyAvailability = getPartyAvailability(json, guests, roomType)
                    this.stayRules = json.stayRules ?? null
                    this.revalidateStay()
                    this.isGettingAvailability = false
                })
        },
        // new rules, after a room type or party change, can refuse the dates chosen under the old ones
        revalidateStay () {
            if (!this.stayRules || !this.arrival) return
            // the rules cover the calendar's year; search accepts later dates the rules never judged
            if (this.arrival < getDateString(this.startDate) || this.arrival > getDateString(this.endDate)) return
            if (!this.stayRules.stays[this.arrival]) {
                this.arrival = this.departure = null
            } else if (this.departure && !isRuleStay(this.stayRules, this.arrival, this.departure)) {
                this.departure = getDefaultDeparture(this.stayRules, this.arrival)
            }
        },
        publishGuests () {
            const guests = { adults: this.numberOfGuests, children: this.childAges.length }
            if (store.guests?.adults !== guests.adults || store.guests?.children !== guests.children) {
                store.guests = guests
            }
        },
        updateArrival ($event) {
            const date = $event?.detail.date
            if (date) {
                const arrival = date.toISOString().slice(0,10)
                // a stay past 28 nights stays chosen, as in updateDeparture(), so the search buttons can say why
                if (date >= this.departureDate || !isRuleStay(this.stayRules, arrival, this.departure)) {
                    this.departure = null
                }
                this.arrival = arrival
                // a weekly resort fills in its week; a click past it still picks a longer stay
                this.departure ??= getDefaultDeparture(this.stayRules, arrival)
            } else {
                this.arrival = this.departure = null
            }
        },
        updateDeparture ($event) {
            const date = $event?.detail.date
            if (date) {
                if (date <= this.arrivalDate) {
                    this.departure = null
                    this.updateArrival($event)
                    return
                }
                // past 28 nights the dates stay chosen and the search buttons say why they are disabled
                this.departure = date.toISOString().slice(0,10)
                // closing the picker restores the dates it opened with, which newer rules may refuse
                this.revalidateStay()
            } else {
                this.departure = null
            }
        },
        updateGuests ($event) {
            this.numberOfGuests = $event.detail.adults
            this.childAges = $event.detail.children
            this.publishGuestsDebounced()
        },
        onSelectRatePlan ($event) {
            this.points = $event.detail.ratePlan === 'points' ? 1 : 0
        }
    },
    watch: {
        arrival (newVal) {
            this.checkInDate = newVal && toLongDateString(newVal)
        },
        departure (newVal) {
            this.checkOutDate = newVal && toLongDateString(newVal)
        },
        childAges () {
            this.numberOfChildren = this.childAges.length
        },
        points () {
            if (this.isMounted) {
                this.submit()
            }
        },
    },
    // lifecycle
    created () {
        // property
        const { hasAvailabilityData, propertyRentalSourceId, propertyCode, roomTypes, slug: propertySlug, timeZone } = JSON.parse(this.property)
        // date
        this.startDate = todayAt(timeZone)
        this.endDate = new Date(Date.UTC(
            this.startDate.getUTCFullYear() + 1,
            this.startDate.getUTCMonth(),
            this.startDate.getUTCDate() - 1,
        ))
        this.propertyRentalSourceId = propertyRentalSourceId
        this.propertyCode = propertyCode
        this.propertySlug = propertySlug
        // room types
        this.roomTypeOptions = roomTypes.sort()
            .map(x => ({
                display: x > 0 ? `${x} Bedroom` : x < 0 ? 'Hotel Room' : 'Studio',
                value: x,
            }))
        this.roomTypeOptions.push({ display: 'All', value: null })
        // search
        const search = JSON.parse(this.search)
        const minBedrooms = Number(search.minBedrooms)
        const hasValidMinBedrooms = search.minBedrooms !== null
            && Number.isFinite(minBedrooms)
            && this.roomTypeOptions.some(o => o.value === minBedrooms)
        this.roomType = hasValidMinBedrooms ? minBedrooms : null
        if (search.checkInDate) {
            this.arrival = toISODateString(search.checkInDate)
        }
        if (search.checkOutDate) {
            this.departure = toISODateString(search.checkOutDate)
        }
        const { minBedrooms: _ignored, ...searchRest } = search
        Object.assign(this, searchRest)
        // user
        Object.assign(this, JSON.parse(this.user))
        // guests: GuestFields shows 2 adults when the search names none
        this.numberOfGuests = this.numberOfGuests || 2
        this.childAges = this.childAges ?? []
        this.publishGuests()
        this.publishGuestsDebounced = debounce(this.publishGuests, 300)
        // availability
        this.availabilityRequestId = 0
        this.hasAvailabilityData = hasAvailabilityData
        this.updateAvailability()
        this.$watch(() => store.guests, () => this.updateAvailability())
        // visible before the page-load fetch starts, or Google's crawler may read the page before the rates exist
        this.loading = this.willSearch()
    },
    mounted () {
        this.submit(true)
        this.isMounted = true
    },
    // misc.
    components: {
        AlertBanner: AlertBannerComponent,
        Btn: BtnComoponent,
        DateFields: DateFieldsComponent,
        Dropdown: DropdownComponent,
        GuestFieldsSelect: GuestFieldsComponent,
        RatePlanToggle: RatePlanToggleComponent,
        DisabledTip: DisabledTipComponent,
    },
}

/**
 * @param {string} longDateString - A date string (M j, Y)
 * @example
 * // returns '1970-01-01'
 * toISODateString('Jan 1, 1970')
 * @returns {string} Returns a date string (Y-m-d)
 */
function toISODateString (longDateString) {
    const date = new Date(longDateString + ' 00:00:00 GMT')
    return date.toISOString().slice(0,10)
}

/**
 * @param {string} isoDateString - A date string (Y-m-d)
 * @example
 * // returns 'Jan 1, 1970'
 * toISODateString('1970-01-01')
 * @returns {string} Returns a date string (M j, Y)
 */
function toLongDateString (isoDateString) {
    const date = new Date(isoDateString)
    const tokens = date.toUTCString().split(' ')
    return `${tokens[2]} ${parseInt(tokens[1])}, ${tokens[3]}`
}
</script>

<template>
    <div class="tw:mx-auto tw:py-8 tw:space-y-8 tw:container-inner wider">
        <AlertBanner
            class="tw:lg:mx-32"
            v-bind="{ text: banner }"
        />
        <!-- search form; with no warning, its empty region above must not push the form down -->
        <form
            class="tw:space-y-8"
            v-bind:class="{ 'tw:mt-0!': !banner }"
            v-on:submit.prevent="submit(false)"
            v-bind:action="action"
        >
            <!-- dollars/points toggle -->
            <RatePlanToggle
                class="tw:text-right"
                v-if="hasRatePlanToggle"
                v-on:select-rate-plan="onSelectRatePlan($event)"
                v-bind="{ ratePlan }"
            />

            <!-- date/guest fieldsets & submit btn -->
            <div
                class="tw:px-4 tw:py-8 tw:space-y-4 tw:bg-e6 tw:lg:px-32"
                v-bind:class="{ 'tw:cursor-wait': loading }"
            >
                <div
                    class="
                        tw:relative
                        tw:space-y-3
                        tw:lg:grid
                        tw:lg:grid-cols-[2fr_1fr_1fr]
                        tw:lg:gap-3
                        tw:lg:space-y-0
                    "
                >
                    <DateFields
                        v-on:update-arrival="updateArrival"
                        v-on:update-departure="updateDeparture"
                        v-on:update-search="submit"
                        v-on:visible-months="visibleMonths = $event.detail.months"
                        v-bind="{
                            revertExclusionSelectors: ['#' + submitBtnId],
                            // availability
                            availability,
                            hasAvailabilityData,
                            isGettingAvailability,
                            stayRules,
                            stayLength,
                            stayLengthMax,
                            // dates
                            arrival,
                            departure,
                            startDate,
                            endDate,
                            // rate plan
                            ratePlan,
                        }"
                        v-bind:fields="datefields"
                    >
                        <template v-slot:datepickerHeader>
                            <Dropdown
                                class="
                                    tw:ml-0
                                    tw:w-full
                                    tw:sm:w-auto
                                "
                                v-if="hasAvailabilityData"
                                v-bind="roomTypeDropdown"
                                v-on:select="setRoomType($event.detail.value)"
                            />
                        </template>
                    </DateFields>
                    <GuestFieldsSelect
                        v-on:update-guests="updateGuests"
                        v-bind:guests="guests"
                    />
                    <!-- the tip opens below, where on a phone it covers nothing -->
                    <!-- only while the calendar is closed: a tap reaching the page would close it and revert the dates, and its Update Search explains instead -->
                    <DisabledTip
                        v-bind="{
                            isActive: isTooLong && !visibleMonths,
                            tooltip: { text: `Cannot book more than ${stayLengthMax} nights`, align: 'end', maxWidth: 320, placement: 'bottom' },
                        }"
                    >
                        <Btn
                            v-bind="{
                                id: submitBtnId,
                                isSecondary: true,
                                isDisabled: isSearchDisabled,
                                type: 'submit',
                            }"
                            v-bind:class="{
                                'tw:cursor-wait': loading,
                                'tw:mix-blend-multiply': isSearchDisabled,
                            }"
                        >
                            Check Availability
                        </Btn>
                    </DisabledTip>
                </div>
            </div>
        </form>

        <!-- loading msg -->
        <div
            v-show="loading"
            data-nav-loading-indicator="true"
            class="
                tw:flex
                tw:flex-col
                tw:justify-center
                tw:items-center
                tw:px-8
                tw:py-4
                tw:space-y-4
                tw:text-center
                tw:bg-white
                tw:border
                tw:border-solid
                tw:border-e6
            "
        >
            <div class="tw:text-2xl">Searching for your next stay!</div>
            <div class="tw:loader"></div>
        </div>

        <!-- no search query msg -->
        <div
            v-show="!stayLength"
            class="
                tw:px-8
                tw:py-4
                tw:space-y-4
                tw:text-center
                tw:bg-white
                tw:border
                tw:border-solid
                tw:border-e6
            "
        >
            <div class="tw:text-2xl">Please enter your travel dates.</div>
            <p>We need your check-in and check-out dates to look up availability and pricing of residences at this resort.</p>
        </div>

        <!-- results -->
        <div
            v-html="resultsHTML"
            v-show="resultsHTML"
            v-bind:x-data="JSON.stringify({ isPointsMember, loggedIn, points })"
        ></div>

        <!-- error msg -->
        <div
            v-show="resultsError"
            v-html="resultsErrorMarkup"
            class="
                tw:px-8
                tw:py-4
                tw:space-y-4
                tw:text-center
                tw:bg-white
                tw:border
                tw:border-solid
                tw:border-e6
                tw:[&>h3]:text-2xl
                tw:[&>h3]:font-normal
            "
        ></div>
    </div>
</template>
